export type PartyId = 'S' | 'M' | 'SD' | 'C' | 'V' | 'KD' | 'L' | 'MP';

// Colors sourced from official party Wikidata entries (sRGB hex triplets).
export const PARTIES: Record<PartyId, { name: string; color: string }> = {
  S:  { name: 'Socialdemokraterna',  color: '#E8112D' },
  M:  { name: 'Moderaterna',         color: '#52BDEC' },
  SD: { name: 'Sverigedemokraterna', color: '#DDDD00' },
  C:  { name: 'Centerpartiet',       color: '#124838' },
  V:  { name: 'Vänsterpartiet',      color: '#B00000' },
  KD: { name: 'Kristdemokraterna',   color: '#000077' },
  L:  { name: 'Liberalerna',         color: '#006AB3' },
  MP: { name: 'Miljöpartiet',        color: '#53A045' },
};

export interface District {
  id: string;
  name: string;
  population: number;
  neutralCount: number;
  followerCounts: Record<PartyId, number>;
  ownedByParty: PartyId | null;
  claimedAt: number | null;
  municipality_code: string;
  vk_code: string;
}

export const PARTY_ORDER: PartyId[] = ['S', 'M', 'SD', 'C', 'V', 'KD', 'L', 'MP'];

export interface ValkrtsAggregate {
  vk_code: string;
  name: string;
  municipality_code: string;
  municipality_name: string;
  county_code: string;
  total_population: number;
  followerCounts: Partial<Record<PartyId, number>>;
  ownedByParty: PartyId | null;
}

export interface CountyAggregate {
  county_code: string;
  county_name: string;
  total_population: number;
  followerCounts: Partial<Record<PartyId, number>>;
  ownedByParty: PartyId | null;
}

export interface AggregateFlipEntry {
  districtId: string;
  districtName: string;
  municipalityCode: string;
  fromParty: PartyId | null;
  toParty: PartyId;
  at: { toMillis: () => number };
  population: number;
}

export interface AggregateContestedEntry {
  districtId: string;
  districtName: string;
  followerCounts: Partial<Record<PartyId, number>>;
  population: number;
}

export const COUNTY_NAMES: Record<string, string> = {
  '01': 'Stockholm',
  '03': 'Uppsala',
  '04': 'Södermanland',
  '05': 'Östergötland',
  '06': 'Jönköping',
  '07': 'Kronoberg',
  '08': 'Kalmar',
  '09': 'Gotland',
  '10': 'Blekinge',
  '12': 'Skåne',
  '13': 'Halland',
  '14': 'Västra Götaland',
  '17': 'Värmland',
  '18': 'Örebro',
  '19': 'Västmanland',
  '20': 'Dalarna',
  '21': 'Gävleborg',
  '22': 'Västernorrland',
  '23': 'Jämtland',
  '24': 'Västerbotten',
  '25': 'Norrbotten',
};

export interface GlobalAggregate {
  updatedAt: { toMillis: () => number };
  totals: {
    districtsByParty: Partial<Record<PartyId, number>>;
    populationByParty: Partial<Record<PartyId, number>>;
    /** Population of districts where the party has the most followers. Absent until the cron has run on the new code. */
    populationLeadingByParty?: Partial<Record<PartyId, number>>;
    neutralDistricts: number;
    /** Districts where every neutral voter has been mobilized (neutralCount === 0). */
    fullyMobilizedDistricts?: number;
    totalDistricts: number;
    totalPopulation: number;
    lansByParty?: Partial<Record<PartyId, string[]>>;
    fullyOwnedLansByParty?: Partial<Record<PartyId, string[]>>;
    /** Share (%) of players who picked each party. Counts/total are withheld server-side. */
    usersByPartyPct?: Partial<Record<PartyId, number>>;
  };
  recentFlips: AggregateFlipEntry[];
  mostContested: AggregateContestedEntry[];
  districtOwnership?: Record<string, PartyId>;
  districtMajority?: Record<string, PartyId>;
  countyDistricts?: Record<string, Partial<Record<PartyId, number>>>;
}
